import { PaletteMode, PaletteOptions, createTheme } from '@mui/material';
import { VISUAL_REFRESH } from 'common/utils';

import { breakpoints } from './breakpoints';
import { components } from './components';
import { palette } from './palette';
import { refreshComponents, refreshPalette, refreshTypography } from './refresh';
import { typography } from './typography';

/**
 * The single seam for the Design System 2.0 visual refresh (HNS-259).
 *
 * Every themed surface in the app resolves through here — `NatterThemeProvider`,
 * `NatterDarkThemeProvider` and `withTheme()` (15 call sites), the `renderWith*` test helpers, and
 * Storybook's `ThemeDecorator`. Switching the factory therefore flips the whole product at once,
 * with no call-site changes and no per-screen conditionals to unpick later.
 *
 * Note this does NOT cover `./shadows`, which 22 files import directly rather than reading off the
 * theme. Removing elevation (HNS-263) has to deal with those separately.
 *
 * `visualRefresh` defaults to the session-wide resolved flag and exists to be overridden: Storybook's
 * toolbar switch renders both worlds side by side, and a test can assert the refreshed theme without
 * mocking a module constant.
 *
 * It is also written onto the theme, so the handful of surfaces that hard-code a value the theme
 * cannot reach can branch on `theme.visualRefresh` and stay in step with the toolbar and with tests
 * — see `./types`. Prefer swapping a `refresh*` export over adding one of those branches.
 */
export const theme = (color?: string, mode?: PaletteMode, visualRefresh: boolean = VISUAL_REFRESH) =>
  visualRefresh
    ? createTheme({
        visualRefresh,
        components: refreshComponents,
        palette: refreshPalette(color, mode) as PaletteOptions,
        typography: refreshTypography,
        breakpoints,
      })
    : createTheme({
        visualRefresh,
        components,
        palette: palette(color, mode) as PaletteOptions,
        typography,
        breakpoints,
      });

export { DEFAULT_TOOLBAR_HEIGHT } from './components/MuiToolbar';
export * from './shadows';
export * from './utils';
